package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.historyLink
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.uikit.components.dropdown.EsDropDownMenu
import com.essntl.uikit.components.dropdown.EsDropdownMenuItem
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDate
import core.dateformat.ui.formatDateRange

@Composable
fun ProposalHistoryItem(
    parentProposal: ProposalModel,
    proposalHistory: ProposalModel,
    request: RequestModel?,
    isSelected: Boolean,
    areas: List<AreaModel>?,
    onOpenProposalHistory: () -> Unit,
    onCopyProposalHistory: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val interactionSource = remember { MutableInteractionSource() }

    val isHovered by interactionSource.collectIsHoveredAsState()

    val isPressed by interactionSource.collectIsPressedAsState()

    var isActionsExpanded by rememberSaveable {
        mutableStateOf(false)
    }

    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier =
            modifier
                .background(
                    when {
                        isSelected ->
                            MaterialTheme.esColorScheme.tintedPressed

                        isPressed ->
                            MaterialTheme.esColorScheme.tintedPressed

                        isHovered ->
                            MaterialTheme.esColorScheme.tintedHover

                        else ->
                            MaterialTheme.esColorScheme.bgSecondaryHover
                    },
                )
                .pointerHoverIcon(PointerIcon.Hand)
                .focusProperties { canFocus = false }
                .clickable(
                    interactionSource = interactionSource,
                    indication = null,
                    onClick = onOpenProposalHistory,
                )
                .padding(
                    vertical = EsConstants.ListRowVerticalPadding,
                    horizontal = EsConstants.ListRowHorizontalPadding,
                )
                .height(EsConstants.ListRowItemHeight),
    ) {
        // Proposal title
        Box(
            contentAlignment = Alignment.CenterStart,
            modifier =
                Modifier
                    .weight(2f)
                    .fillMaxHeight(),
        ) {
            Text(
                text = "v${proposalHistory.version}",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.p2Medium14,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // Owner
        ProposalRowItem(
            modifier = Modifier.width(80.dp),
        )

        // Destination
        ProposalVersionRowDefaultItem(
            text =
                areas
                    ?.map { it.name ?: "" }
                    ?.filter { it.isNotEmpty() }
                    ?.joinToString(", ")
                    ?.ifEmpty { "Unknown destination" },
        )

        // Dates
        val dateDiff = rememberNumberOfDaysPrettyString(
            proposalHistory.fromDate,
            proposalHistory.toDate,
        )

        ProposalVersionRowDefaultItem(
            text =
                listOf(
                    proposalHistory.fromDate,
                    proposalHistory.toDate,
                ).formatDateRange() + '\n' + dateDiff,
            maxLines = 2,
        )

        // Version description placeholder
        ProposalRowItem {
            Text(
                text = proposalHistory.description.orEmpty(),
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.c1Medium13,
                maxLines = 3,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // version
        ProposalRowItem(
            modifier = Modifier.weight(0.5f),
        ) {
            Text(
                text = "v${proposalHistory.version}",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p2Medium14,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // Actions
        ProposalRowItem {
            Row(
                horizontalArrangement = Arrangement.spacedBy(1.5f.spacing),
            ) {
                val uriHandler = LocalUriHandler.current

                EsSmallOutlineIconButton(
                    onClick = { isActionsExpanded = !isActionsExpanded },
                    iconRes = EsIcons.MoreHorizontal,
                    modifier = Modifier.focusProperties { canFocus = false },
                )

                EsDropDownMenu(
                    expanded = isActionsExpanded,
                    onDismissRequest = {
                        isActionsExpanded = false
                    },
                    modifier = Modifier.width(250.dp),
                ) {
                    EsDropdownMenuItem(
                        text = "Open this version in web browser",
                        textColor = MaterialTheme.esColorScheme.textTertiary,
                        prefixIcon = EsIcons.Browse,
                        iconColor = MaterialTheme.esColorScheme.iconDisabled,
                        onClick = {
                            uriHandler.openUri(
                                uri = proposalHistory.historyLink,
                            )

                            isActionsExpanded = false
                        },
                    )

                    EsDropdownMenuItem(
                        text = "Reset Master to this version",
                        textColor = MaterialTheme.esColorScheme.textTertiary,
                        prefixIcon = EsIcons.Share,
                        iconColor = MaterialTheme.esColorScheme.iconDisabled,
                        enabled = parentProposal.status != ProposalModel.Status.Confirmed,
                        onClick = {
                            onCopyProposalHistory()
                            isActionsExpanded = false
                        },
                    )
                }
            }
        }

        // Created
        ProposalVersionRowDefaultItem(
            text = proposalHistory.createdAt.formatDate("."),
            modifier = Modifier.weight(0.7f),
        )

        // Last changed
        ProposalVersionRowDefaultItem(
            text = proposalHistory.updatedAt.orEmpty().formatDate("."),
            modifier = Modifier.weight(0.7f),
        )

        // Request Date
        ProposalVersionRowDefaultItem(
            text = request?.createdAt?.formatDate(".") ?: proposalHistory.createdAt.formatDate("."),
            modifier = Modifier.weight(0.7f),
        )
    }
}

@Composable
private fun RowScope.ProposalVersionRowDefaultItem(
    text: String?,
    maxLines: Int = 1,
    modifier: Modifier = Modifier.weight(1f),
) {
    ProposalRowItem(
        modifier = modifier,
    ) {
        Text(
            text = text ?: "",
            color = MaterialTheme.esColorScheme.textPrimary,
            style = MaterialTheme.esTypography.p2Regular14,
            maxLines = maxLines,
            overflow = TextOverflow.Ellipsis,
        )
    }
}
